import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import HomeIcon from '@mui/icons-material/Home'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LogoutIcon from '@mui/icons-material/Logout'
import styled from 'styled-components'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  position: relative;
`

const ErrorCard = styled(Box)`
  background: white;
  border-radius: 24px;
  padding: 3rem 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  @media (max-width: 600px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
`

const IconContainer = styled(Box)`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  .MuiSvgIcon-root {
    font-size: 80px;
    color: #a864ff;
    opacity: 0.8;
  }
`

const Title = styled(Typography)`
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 1rem;
  
  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`

const Message = styled(Typography)`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`

const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: center;
  }
`

const StyledButton = styled(Button)`
  text-transform: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  
  &.primary-button {
    background: linear-gradient(135deg, #a864ff 0%, #8b46ff 100%);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, #8b46ff 0%, #7a3fef 100%);
      box-shadow: 0 4px 12px rgba(168, 100, 255, 0.4);
    }
  }
  
  &.secondary-button {
    color: #666;
    border-color: #ddd;
    
    &:hover {
      background: #f5f5f5;
      border-color: #a864ff;
      color: #a864ff;
    }
  }
`

const LogoutButtonContainer = styled(Box)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  
  @media (max-width: 600px) {
    top: 0.75rem;
    right: 0.75rem;
  }
`

export default function InvalidParticipantErrorPage({ bookedEventId }) {
  const navigate = useNavigate()
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)

  const handleGoHome = () => {
    navigate('/')
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true)
  }

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false)
    logoutHandler()
    navigate('/')
  }

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false)
  }

  return (
    <ErrorContainer>
      <LogoutButtonContainer>
        <Tooltip title="Logout" arrow>
          <IconButton
            onClick={handleLogoutClick}
            sx={{
              color: '#ef4444',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            aria-label="Logout"
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </LogoutButtonContainer>
      <ErrorCard>
        <IconContainer>
          <LockIcon />
        </IconContainer>
        
        <Title variant="h4">
          Access Restricted
        </Title>
        
        <Message variant="body1">
          You don't have permission to access this session. 
          This may happen if:
        </Message>
        
        <Box sx={{ textAlign: 'left', mb: 3, color: '#666' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • The session link is invalid or expired
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • You're not a participant of this meeting
          </Typography>
          <Typography variant="body2">
            • The session has been cancelled or rescheduled
          </Typography>
        </Box>
        
        <ButtonContainer>
          <StyledButton
            variant="contained"
            className="primary-button"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            fullWidth
          >
            Go to Home
          </StyledButton>
          
          <StyledButton
            variant="outlined"
            className="secondary-button"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            fullWidth
          >
            Go Back
          </StyledButton>
        </ButtonContainer>
      </ErrorCard>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorContainer>
  )
}

